import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check, ShieldCheck, ArrowLeft } from "lucide-react";
import { useI18n } from "i18n";
import LanguageSwitcher from "components/LanguageSwitcher";
import portalApi, { setPortalToken } from "lib/portalApi";

// /pub/register — self-registration in the order the product requires:
// data -> a DISTINCT code to each channel -> password -> MFA -> (then the mandatory face).
const STEPS = ["data", "codes", "password", "mfa"];

function Stepper({ current, t }) {
  const label = {
    data: t("pub.reg.stepData", "Dados"),
    codes: t("pub.reg.stepCodes", "Verificação"),
    password: t("pub.reg.stepPassword", "Senha"),
    mfa: t("pub.reg.stepMfa", "MFA"),
  };
  return (
    <ol className="flex items-center gap-2 mb-6 text-xs">
      {STEPS.map((s, i) => {
        const done = STEPS.indexOf(current) > i;
        const active = s === current;
        return (
          <li key={s} className="flex items-center gap-2">
            <span className={`h-6 w-6 rounded-full grid place-items-center border text-[11px]
              ${done ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-500"
                     : active ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
              {done ? <Check className="h-3 w-3" /> : i + 1}
            </span>
            <span className={active ? "text-foreground" : "text-muted-foreground"}>{label[s]}</span>
            {i < STEPS.length - 1 && <span className="w-4 h-px bg-border" />}
          </li>
        );
      })}
    </ol>
  );
}

function CodeBox({ chan, state, onVerify, t, busy }) {
  const [code, setCode] = useState("");
  if (state.verified) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2.5 text-sm">
        <ShieldCheck className="h-4 w-4 text-emerald-500" />
        <span className="text-emerald-500">{chan.label}</span>
        <span className="text-muted-foreground truncate">{chan.target}</span>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-sm font-medium mb-0.5">{chan.label}</div>
      <div className="text-xs text-muted-foreground mb-2 truncate">
        {state.sent
          ? t("pub.reg.codeSent", "Código enviado para {to}").replace("{to}", chan.target)
          : t("pub.reg.codeNotSent", "Não foi possível enviar para {to}").replace("{to}", chan.target)}
      </div>
      <div className="flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={6}
          placeholder="A1B2C3" autoComplete="one-time-code"
          className="w-36 h-10 rounded-lg border border-border bg-background text-center font-mono tracking-[0.25em] text-sm" />
        <button type="button" disabled={busy || code.trim().length < 4}
          onClick={() => onVerify(code.trim())}
          className="h-10 px-3 rounded-lg border border-border text-sm hover:bg-muted/50 disabled:opacity-40">
          {t("pub.reg.confirm", "Confirmar")}
        </button>
      </div>
    </div>
  );
}

export default function PortalRegister() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState("data");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [reg, setReg] = useState(null);       // {reg_id, email:{...}, phone:{...}}
  const [chan, setChan] = useState({ email: { sent: false, verified: false },
                                     phone: { sent: false, verified: false } });
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [mfa, setMfa] = useState(null);       // {uri, qr}
  const [mfaCode, setMfaCode] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const start = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await portalApi.post("/register/start/", { ...form, language: lang });
      setReg(r.data);
      setChan({ email: { sent: r.data.email.sent, verified: false },
                phone: { sent: r.data.phone.sent, verified: false } });
      setStep("codes");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(d === "email_taken" ? t("pub.reg.emailTaken", "Este e-mail já tem conta.")
        : d === "invalid_phone" ? t("pub.reg.badPhone", "Telefone inválido.")
        : d === "rate_limited" ? t("pub.reg.rate", "Muitas tentativas. Aguarde alguns minutos.")
        : t("pub.reg.startFailed", "Não foi possível iniciar o cadastro."));
    } finally { setBusy(false); }
  };

  const verify = (kind) => async (code) => {
    setBusy(true); setError("");
    try {
      const r = await portalApi.post("/register/verify/", {
        verification_id: reg[kind].verification_id, code });
      setChan((c) => ({ ...c, [kind]: { ...c[kind], verified: true } }));
      if (r.data.email_verified && r.data.phone_verified) setStep("password");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(d === "mismatch" ? t("pub.reg.codeWrong", "Código incorreto.")
        : d === "expired" ? t("pub.reg.codeExpired", "Código expirado.")
        : t("pub.reg.codeFailed", "Não foi possível confirmar."));
    } finally { setBusy(false); }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (password !== password2) {
      setError(t("pub.reg.mismatch", "As senhas não conferem."));
      return;
    }
    setBusy(true); setError("");
    try {
      const r = await portalApi.post("/register/password/", { reg_id: reg.reg_id, password });
      setMfa(r.data.mfa);
      setStep("mfa");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? d.join(" ")
        : d === "email_taken" ? t("pub.reg.emailTaken", "Este e-mail já tem conta.")
        : t("pub.reg.passwordFailed", "Senha recusada pela política."));
    } finally { setBusy(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await portalApi.post("/register/mfa/", { reg_id: reg.reg_id, code: mfaCode.trim() });
      setPortalToken(r.data.token);
      navigate("/pub/enroll", { replace: true });     // the face is mandatory
    } catch {
      setError(t("pub.reg.mfaWrong", "Código do autenticador inválido."));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7] px-4 py-8">
      <div className="w-full max-w-[460px]">
        <div className="flex justify-end mb-3">
          <LanguageSwitcher />
        </div>
        <div className="bg-white rounded-2xl shadow-lg border-0 p-6">
          <button type="button" onClick={() => navigate("/pub/login")}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
            <ArrowLeft className="h-4 w-4" />{t("pub.reg.back", "Voltar ao login")}
          </button>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 mb-1">{t("pub.reg.title", "Criar minha conta")}</h1>
          <p className="text-sm text-gray-500 mb-5">
            {t("pub.reg.subtitle", "Confirmamos seu e-mail e seu celular, você define a senha e o MFA, e por fim cadastra seu rosto.")}
          </p>
          <Stepper current={step} t={t} />

          {step === "data" && (
            <form onSubmit={start} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)}
                  placeholder={t("pub.reg.firstName", "Nome")} autoFocus
                  className="h-11 rounded-lg border border-border bg-background text-sm px-3" />
                <input value={form.last_name} onChange={(e) => set("last_name", e.target.value)}
                  placeholder={t("pub.reg.lastName", "Sobrenome")}
                  className="h-11 rounded-lg border border-border bg-background text-sm px-3" />
              </div>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                placeholder={t("pub.reg.email", "E-mail")} autoComplete="email"
                className="w-full h-11 rounded-lg border border-border bg-background text-sm px-3" />
              <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                placeholder={t("pub.reg.phone", "Celular (WhatsApp)")} autoComplete="tel"
                className="w-full h-11 rounded-lg border border-border bg-background text-sm px-3" />
              {error && <p className="text-sm text-red-500">{error}</p>}
              <button type="submit" disabled={busy || !form.first_name || !form.email || !form.phone}
                className="w-full h-11 rounded-lg bg-primary text-primary-foreground text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("pub.reg.sendCodes", "Enviar códigos")}
              </button>
            </form>
          )}

          {step === "codes" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t("pub.reg.codesHelp", "Enviamos um código diferente para cada canal. Confirme os dois.")}
              </p>
              <CodeBox t={t} busy={busy} state={chan.email} onVerify={verify("email")}
                chan={{ label: t("pub.reg.email", "E-mail"), target: form.email }} />
              <CodeBox t={t} busy={busy} state={chan.phone} onVerify={verify("phone")}
                chan={{ label: t("pub.reg.phone", "Celular (WhatsApp)"), target: form.phone }} />
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          )}

          {step === "password" && (
            <form onSubmit={submitPassword} className="space-y-3">
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder={t("pub.reg.password", "Senha")} autoFocus autoComplete="new-password"
                className="w-full h-11 rounded-lg border border-border bg-background text-sm px-3" />
              <input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)}
                placeholder={t("pub.reg.password2", "Repita a senha")} autoComplete="new-password"
                className="w-full h-11 rounded-lg border border-border bg-background text-sm px-3" />
              {error && <p className="text-sm text-red-500">{error}</p>}
              <button type="submit" disabled={busy || !password || !password2}
                className="w-full h-11 rounded-lg bg-primary text-primary-foreground text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("pub.reg.setPassword", "Definir senha")}
              </button>
            </form>
          )}

          {step === "mfa" && mfa && (
            <form onSubmit={submitMfa} className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t("pub.reg.mfaHelp", "Escaneie o QR no seu app autenticador e digite o código gerado.")}
              </p>
              {mfa.qr && <img src={mfa.qr} alt="MFA QR"
                className="h-44 w-44 mx-auto rounded-lg bg-white p-2" />}
              <input value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                maxLength={6} inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
                className="w-full h-11 rounded-lg border border-border bg-background text-center text-lg font-mono tracking-[0.3em]" />
              {error && <p className="text-sm text-red-500">{error}</p>}
              <button type="submit" disabled={busy || mfaCode.length < 6}
                className="w-full h-11 rounded-lg bg-primary text-primary-foreground text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("pub.reg.finish", "Concluir e cadastrar meu rosto")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
